import { useState, useEffect, useMemo } from 'react'
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { useAuth } from '@contexts/AuthContext'
import { useCityContext } from '@contexts/LocationContext' // Alias to avoid conflict
import { validateEmail, validateCPF } from '@utils/validators'
import { formatCPF } from '@utils/formatters'
import { getWhatsappLink } from '@config/contacts'
import { DOCUMENT_TYPES } from '@config/constants'
import { Button, Loading, Modal, Dropdown } from '@components'
import { ENABLE_GOOGLE_LOGIN } from '@config/features'
import api from '@services/api'
import './Login.css'

const Login = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { signIn, signInWithGoogle, handleGoogleCallback, recoverPassword, isAuthenticated, loading, error, clearError } = useAuth()
  const { city, isRibeirao } = useCityContext()
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  })
  const [formErrors, setFormErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  
  // Forgot Password Modal state
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false)
  const [forgotPasswordStep, setForgotPasswordStep] = useState(1) // 1: Document, 2: Email Confirmation
  const [forgotPasswordData, setForgotPasswordData] = useState({
    documentType: 'CPF',
    document: '',
    email: ''
  })
  const [maskedEmail, setMaskedEmail] = useState('')
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false)
  const [forgotPasswordError, setForgotPasswordError] = useState('')

  // NEW: Determine WhatsApp URL dynamically for the modal
  const whatsappUrl = useMemo(() => {
    if (city?.slug) {
      return getWhatsappLink(city.slug)
    }
    return getWhatsappLink('uberlandia') // Default if no city selected
  }, [city?.slug])

  // Redireciona se já estiver autenticado
  useEffect(() => {
    if (isAuthenticated) {
      // Se tiver uma origem definida (ex: clicou em "Vou" sem estar logado), volta para lá.
      // Senão, se estiver em uma rota de cidade, vai para a home da cidade.
      // Caso contrário, vai para a seleção de cidades (/).
      const defaultPath = city?.slug ? `/${city.slug}` : '/';
      const from = location.state?.from?.pathname || defaultPath;
      navigate(from, { replace: true })
    }
  }, [isAuthenticated, navigate, location, city?.slug])

  // Limpa erros ao mudar de campo
  useEffect(() => {
    clearError()
  }, [formData, clearError])

  const handleChange = (e) => {
    const { name, value } = e.target
    let formattedValue = value

    if (name === 'email') {
      const cleaned = value.replace(/\D/g, '')
      const isDigitsOnlyOrCpfLike = /^[0-9.-]*$/.test(value)
      if (isDigitsOnlyOrCpfLike && cleaned.length > 0) {
        const truncated = cleaned.slice(0, 11)
        formattedValue = formatCPF(truncated)
      }
    }

    setFormData(prev => ({ ...prev, [name]: formattedValue }))
    
    // Limpa erro do campo
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: null }))
    }
  }

  const validateForm = () => {
    const errors = {}
    
    if (!formData.email) {
      errors.email = 'E-mail ou CPF é obrigatório'
    } else {
      const isEmail = formData.email.includes('@')
      if (isEmail) {
        if (!validateEmail(formData.email)) {
          errors.email = 'E-mail inválido'
        }
      } else {
        const cleanedCPF = formData.email.replace(/\D/g, '')
        if (!validateCPF(cleanedCPF)) {
          errors.email = 'CPF inválido'
        }
      }
    }
    
    if (!formData.password) {
      errors.password = 'Senha é obrigatória'
    }
    
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) return
    
    setIsSubmitting(true)
    
    const isEmail = formData.email.includes('@')
    const loginValue = isEmail ? formData.email : formData.email.replace(/\D/g, '')
    
    // A navegação será tratada pelo useEffect que observa a mudança em `isAuthenticated`
    await signIn(loginValue, formData.password)
    
    // Se o signIn falhar, o estado `error` no AuthContext será populado,
    // e a UI exibirá a mensagem de erro. Se for bem-sucedido, o useEffect fará o redirect.
    setIsSubmitting(false)
  }

  const handleGoogleLogin = () => {
    signInWithGoogle()
  }

  // Forgot Password handlers
  const handleForgotPasswordNext = async (e) => {
    e.preventDefault()
    setForgotPasswordError('')
    
    if (!forgotPasswordData.document) {
      setForgotPasswordError('Documento é obrigatório')
      return
    }

    setForgotPasswordLoading(true)
    try {
      const response = await api.get('/users/email', { 
        main_document: forgotPasswordData.document.replace(/\D/g, '') 
      })
      setMaskedEmail(response.email)
      setForgotPasswordStep(2)
    } catch (err) {
      setForgotPasswordError(err.message || 'Usuário não encontrado')
    } finally {
      setForgotPasswordLoading(false)
    }
  }

  const handleCloseForgotPasswordModal = () => {
    setShowForgotPasswordModal(false)
    setForgotPasswordStep(1)
    setForgotPasswordData({
      documentType: 'CPF',
      document: '',
      email: ''
    })
    setMaskedEmail('')
    setForgotPasswordError('')
  }

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault()
    setForgotPasswordError('')

    if (!forgotPasswordData.email) {
      setForgotPasswordError('E-mail é obrigatório')
      return
    }

    if (!validateEmail(forgotPasswordData.email)) {
      setForgotPasswordError('E-mail inválido')
      return
    }

    setForgotPasswordLoading(true)
    const result = await recoverPassword(
      forgotPasswordData.document.replace(/\D/g, ''),
      forgotPasswordData.email,
      city?.slug || 'uberlandia'
    )

    if (result.success) {
      handleCloseForgotPasswordModal()
    } else {
      setForgotPasswordError(result.error || 'Erro ao processar solicitação')
    }
    setForgotPasswordLoading(false)
  }

  if ((loading && !isSubmitting) || isAuthenticated) {
    return (
      <div className="login-page login-page--loading">
        <Loading size="lg" text="Carregando..." />
      </div>
    )
  }

  return (
    <main className="login-page">
      <div className="login-page__container">
        <div className="login-page__content">
          {/* Logo */}
          <Link to={`/${city?.slug || ''}/`} className="login-page__logo">
            <img 
              src={isRibeirao ? '/icon-rib.png' : '/icon-udi.png'} 
              alt="Arqzin Ingressos" 
              className="login-page__logo-image" 
            />
          </Link>

          <div className="login-page__card">
            <div className="login-page__header">
              <h1 className="login-page__title">Entrar</h1>
              <p className="login-page__subtitle">
                Acesse sua conta para comprar ingressos e acompanhar seus eventos
              </p>
            </div>

            {/* Erro geral */}
            {error && (
              <div className="login-page__error">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                <span>{error}</span>
              </div>
            )}

            {/* Formulário */}
            <form className="login-page__form" onSubmit={handleSubmit}>
              <div className="login-page__field">
                <label htmlFor="email">E-mail ou CPF</label>
                <input
                  type="text"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="seu@email.com ou 000.000.000-00"
                  autoComplete="username"
                  className={formErrors.email ? 'input--error' : ''}
                />
                {formErrors.email && (
                  <span className="login-page__field-error">{formErrors.email}</span>
                )}
              </div>

              <div className="login-page__field">
                <label htmlFor="password">Senha</label>
                <div className="login-page__password-input">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    className={formErrors.password ? 'input--error' : ''}
                  />
                  <button
                    type="button"
                    className="login-page__password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  >
                    {showPassword ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    )}
                  </button>
                </div>
                {formErrors.password && (
                  <span className="login-page__field-error">{formErrors.password}</span>
                )}
              </div>

              <button
                type="button"
                className="login-page__forgot-link"
                onClick={() => setShowForgotPasswordModal(true)}
              >
                Esqueceu sua senha?
              </button>

              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                loading={isSubmitting}
                disabled={isSubmitting}
              >
                Entrar
              </Button>
            </form>
            {ENABLE_GOOGLE_LOGIN && (
              <>
                <div className="login-page__divider">
                  <span>ou</span>
                </div>

                <div className="login-page__social">
                  <button
                    type="button"
                    className="login-page__social-btn login-page__social-btn--google"
                    onClick={handleGoogleLogin}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    <span>Continuar com Google</span>
                  </button>
                </div>
              </>
            )}

            {/* Link para cadastro */}
            <p className="login-page__register-link">
              Não tem uma conta?{' '}
              <Link to={`/${city?.slug || ''}/cadastro`}>Cadastre-se</Link>
            </p>
          </div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      <Modal
        isOpen={showForgotPasswordModal}
        onClose={handleCloseForgotPasswordModal}
        title="Recuperar Senha"
      >
        <div className="login-page__modal-content">
          {forgotPasswordStep === 1 ? (
            <form onSubmit={handleForgotPasswordNext} className="login-page__modal-form">
              <p className="login-page__modal-text">
                Para recuperar seu acesso, primeiro informe seu documento cadastrado.
              </p>
              
              <div className="login-page__row">
                <Dropdown
                  label="Tipo"
                  id="forgot-doc-type"
                  name="documentType"
                  value={forgotPasswordData.documentType}
                  onChange={(e) => setForgotPasswordData(prev => ({ ...prev, documentType: e.target.value }))}
                  options={DOCUMENT_TYPES}
                />
                <div className="login-page__field">
                  <label htmlFor="forgot-document">Documento</label>
                  <input
                    type="text"
                    id="forgot-document"
                    value={forgotPasswordData.documentType === 'CPF' ? formatCPF(forgotPasswordData.document) : forgotPasswordData.document}
                    onChange={(e) => setForgotPasswordData(prev => ({ ...prev, document: e.target.value }))}
                    placeholder={forgotPasswordData.documentType === 'CPF' ? '000.000.000-00' : 'Número do passaporte'}
                  />
                </div>
              </div>

              {forgotPasswordError && (
                <div className="login-page__field-error" style={{ marginBottom: '1rem' }}>
                  {forgotPasswordError}
                </div>
              )}

              <div className="login-page__modal-actions">
                <Button 
                  type="button" 
                  variant="secondary" 
                  onClick={handleCloseForgotPasswordModal}
                >
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  variant="primary" 
                  loading={forgotPasswordLoading}
                  disabled={forgotPasswordLoading}
                >
                  Continuar
                </Button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleForgotPasswordSubmit} className="login-page__modal-form">
              <p className="login-page__modal-text">
                Encontramos uma conta vinculada ao e-mail: <br />
                <strong style={{ color: 'var(--color-primary)', fontSize: '1.1rem' }}>{maskedEmail}</strong>
              </p>
              
              <p className="login-page__modal-text" style={{ fontSize: '0.9rem', color: 'var(--color-text-secondary)' }}>
                Para sua segurança, informe o e-mail completo para receber o link de recuperação.
              </p>

              <div className="login-page__field">
                <label htmlFor="forgot-email">E-mail Completo</label>
                <input
                  type="email"
                  id="forgot-email"
                  value={forgotPasswordData.email}
                  onChange={(e) => setForgotPasswordData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="seu@email.com"
                  required
                />
              </div>

              {forgotPasswordError && (
                <div className="login-page__field-error" style={{ marginBottom: '1rem' }}>
                  {forgotPasswordError}
                </div>
              )}

              <div className="login-page__modal-actions">
                <Button 
                  type="button" 
                  variant="secondary" 
                  onClick={() => setForgotPasswordStep(1)}
                  disabled={forgotPasswordLoading}
                >
                  Voltar
                </Button>
                <Button 
                  type="submit" 
                  variant="primary" 
                  loading={forgotPasswordLoading}
                  disabled={forgotPasswordLoading}
                >
                  Enviar Link
                </Button>
              </div>
            </form>
          )}
        </div>
      </Modal>
    </main>
  )
}

export default Login
